// A wired port's row, one component for both Operate surfaces that read an
// access point's wired ports (#1353, ADR-0245 decision 5c "on one shared
// component"): the access point's own device page and Operate › Wired's port
// table. They render a row through `AccessPointPortRow` and decide which rows
// there are through `accessPointPortRows`, so the two cannot disagree about a
// port's name, its intended VLAN or what its state cell says — the corpus case
// behind the ruling is three Zenith surfaces rendering one device three ways.
//
// **Which ports an access point has, on both surfaces, is one answer** (the
// question #1353 was given on 2026-10-04, note 3951509812, which ADR-0245
// decision 4a's "by their catalogue model's logical ports" did not settle):
// the ports **its own live port report** names, apart from the one it files
// as its uplink, with every port the site holds intent for that the report
// does not name beside them. That is the vocabulary ADR-0239 decision 4b
// makes an access point's wired ports in, and §11.3's. The catalogue model is
// not the source: it resolves no access point in the field today (#286), and
// a WF-660A has one wired port where an EAP-102 has two (#940), so a shape
// assumed from it would be a guess. The document the cell holds is not
// either: for a generated access point it is the venue's and names no port
// at all (#838).
//
// **Where the report is not read, the rows are the intent's and the cell says
// so.** The device page reads it; the site's table never does — an access
// point's port state is read for one device on its own page and never by the
// site view (§11.3, ADR-0045 decision 6, ADR-0052 decision 7) — so the table
// passes no report, its rows are the ports the site holds intent for, and
// each state cell links to the access point's page, where it is read. Same
// function, same component, one input fewer, and the difference is said on
// the row rather than left to be noticed.
//
// **A port the report does not answer is unanswered, never zero and never
// down** (`conventions/dashboards-as-code.md`).
import { useMemo } from "react";
import { api } from "../../api/client";
import { useQuery, type QueryState } from "../../api/hooks";
import type { components } from "../../api/schema";
import { coarseReportAge } from "../../dashboard/format";
import { ObjectLink } from "../../shell/ObjectLink";
import { ScopeLink } from "../../shell/scope";
import { Card, Info, Loading, Pill, ProblemBox } from "../../ui";
import { State } from "../devices/DevicesTable";
import { notAppliedWords, type NotAppliedPort } from "../devices/family";
import type { Device } from "../devices/commands";
import { unrecognized } from "../../api/openEnum";

type PortIntent = components["schemas"]["PortIntent"];
export type AccessPointLive = components["schemas"]["AccessPointPortStateSet"];
type AccessPointLivePort = components["schemas"]["AccessPointPortState"];

/** The three columns of a wired-port table: the port, its state, its VLAN. */
export const WIRED_COLUMNS = 3;

/** The VLANs a port's intent names, in the vocabulary the intent uses: the
 * untagged VLAN an access port puts frames on or a trunk treats as native,
 * and the VLANs a trunk carries tagged.
 *
 * A port with no intent row says so. That is not VLAN 0 and not the port
 * disabled: it is Zenith declining to describe the port, and an apply then
 * sends the port as the cell holds it.
 *
 * **`status` is why this takes the read's state and not only its row.** The
 * ports are enumerated by one read and their intent comes from another, so a
 * row can be absent because the site holds no intent for the port or because
 * the intent read never answered — and those are opposite facts. Without the
 * status, a failed intent read renders every port on the switch as *no
 * planned settings*, which is a claim about the site's record made from a
 * request that failed.
 *
 * **`family` is whose port the cell is, and it chooses one sentence** (#1678,
 * note 4005663823): what a port with no planned settings gets at the next plan
 * is a different fact for a switch and for an access point, and one tooltip
 * said the switch's of both. */
export function Vlans({ intent, status, family, notApplied, witness }: { intent?: PortIntent; status: string; family: "switch" | "ap"; notApplied?: NotAppliedPort; witness?: string }) {
  if (!intent) {
    if (status === "loading") return <span className="muted">&hellip;</span>;
    if (status === "error") {
      return <span className="muted" title="This device's planned port settings could not be read, so it is not known whether this port has any. That does not mean the port has none.">not read</span>;
    }
    // Restates ADR-0078 decision 1 — a switch port with no intent is sent as the cell holds it, never
    // "the switch keeps what it has". An access point's own document is composed from its port
    // intent over the venue's (`internal/generate/apwiredports.go`, ADR-0239), not merged into a
    // held document, so its sentence says only what the next plan is: nothing was measured on a
    // device. Until #1678 one clause, *"an apply sends the port as the controller holds it"*, was
    // drawn for both; note 4005663823 there rules the two.
    const next = family === "switch"
      ? "applying a plan sends the port as the controller holds it."
      : "the access point's configuration is planned without it.";
    return <span className="muted" title={`This port has no planned settings. That is not VLAN 0 and it does not disable the port: ${next}`}>no planned settings</span>;
  }
  const tagged = intent.tagged_vlans ?? [];
  return (
    <>
      {intent.untagged_vlan !== undefined
        // Restates ADR-0245 decision 4a and ADR-0312 decision 8a — the column carries the VLAN
        // the port's intent names, and intent is never the switch's running state.
        ? <span title={intent.mode === "trunk" ? "The native VLAN planned for this port: the VLAN Design asks untagged frames on this trunk to belong to. It is not read from the switch." : "The access VLAN planned for this port: the VLAN Design asks this port's untagged traffic to belong to. It is not read from the switch."}>{intent.untagged_vlan}</span>
        : <span className="muted" title="This port's planned settings name no untagged VLAN.">&mdash;</span>}
      {tagged.length > 0 && <span className="dim" title="The VLANs this trunk carries tagged."> &middot; tagged {tagged.join(", ")}</span>}
      {!intent.enabled && <span className="dim" title="The planned settings disable this port: applying a plan would stop it passing traffic. The port has planned settings, so this is not a missing reading."> &middot; disabled</span>}
      {notApplied && <> &middot; <State words={{ word: "not applied", tone: "warn", title: notAppliedWords(notApplied) }} /></>}
      {witness && <span className="dim" title="The VLAN the switch's own forwarding table learned hosts on, which the planned settings do not give this port. This can show a disagreement. It never shows agreement."> &middot; {witness}</span>}
    </>
  );
}

/** One access point's wired port, as both surfaces list it. */
export interface AccessPointRow {
  port: string;
  /** The site's intent for the port, when it holds one. */
  intent?: PortIntent;
  /** The port's entry in the device's own report, when the report was read
   * and names it. */
  live?: AccessPointLivePort;
}

/** Which wired ports an access point has, and what is known of each — the
 * one answer both surfaces give (see the head of this file).
 *
 * With a report that answered: every port it names that it does not file as
 * upstream, and every port the site holds intent for that the report does not
 * name. Without one: the ports the site holds intent for. Sorted by port, so
 * both surfaces list a device in one order. The uplink is never a row
 * (§11.3); an intent for a port the report files as upstream is kept, so an
 * intent is never hidden by the report. */
export function accessPointPortRows(intent: readonly PortIntent[] | undefined, live?: AccessPointLive): AccessPointRow[] {
  const rows = new Map<string, AccessPointRow>();
  const answered = live && !live.unanswered ? live : undefined;
  for (const p of answered?.ports ?? []) {
    if (!p.upstream) rows.set(p.port, { port: p.port, live: p });
  }
  for (const p of intent ?? []) {
    const reported = answered?.ports.find((r) => r.port === p.port);
    rows.set(p.port, { port: p.port, intent: p, ...(reported ? { live: reported } : {}) });
  }
  return [...rows.values()].sort((a, b) => a.port.localeCompare(b.port, undefined, { numeric: true }));
}

/** Why an access point's report was not read, in words that say what the
 * device and its cell did rather than what the page could not do. */
// Restates ADR-0219 decision 3 and ADR-0097 — `unknown` is the gateway holding no record and is
// not offline, and a device the one definition does not call online is not asked.
export function accessPointUnansweredWords(reason: string): string {
  switch (reason) {
    case "offline": return "the access point is offline, so it was not asked";
    case "unknown": return "the controller's gateway service holds no record for this access point, so it is not known whether it is up, and it was not asked";
    case "no_report": return "the controller holds no state report for this access point";
    case "unreadable": return "the access point's report could not be read";
    case "silent": return "the access point's report says nothing about its ports";
    // Restates ADR-0511 decision 5a — a reason this build does not know is named as received, and is
    // not read as offline or as `unknown`.
    default: return `the access point's report was not read, for a reason this page does not recognize (${unrecognized(reason)})`;
  }
}

/** Where a row's state comes from: the device's own report, as the device
 * page reads it, or its page, for a surface that does not read it. */
export type RowState = { read: QueryState<AccessPointLive> } | { onItsPage: Device };

/** One port's state cell. */
function StateCell({ row, state }: { row: AccessPointRow; state: RowState }) {
  if ("onItsPage" in state) {
    // Restates ADR-0052 decision 7 and ZENITH.md §11.3 — an access point's port state is read for one
    // device on its own page, and never by the site view.
    return (
      <ObjectLink kind="device" value={state.onItsPage.serial}
        title="An access point's port state is read for one access point at a time, on its own page, and never on a page for the whole site. Open its page to see what this port is doing. Nothing here says the port is down.">
        read on its page
      </ObjectLink>
    );
  }
  const q = state.read;
  if (q.status === "loading" || q.status === "idle") return <span className="muted">&hellip;</span>;
  if (q.status === "error") return <span className="muted">not read</span>;
  const u = q.data.unanswered;
  if (u) return <span className="muted" title={accessPointUnansweredWords(u.reason)}>unanswered &middot; {u.reason}</span>;
  if (!row.live) {
    return <span className="muted" title="The access point's own report names no port by this name, so what it is doing is not known. The port has planned settings here. The device may not have this port, or may call it something else. That does not mean the port is down.">not in its report</span>;
  }
  if (row.live.link === "up") return <><Pill tone="good">up</Pill>{row.live.speed_mbps !== undefined && <> {row.live.speed_mbps} Mb/s</>}</>;
  return <span>down</span>;
}

/** One access point's wired port: its name, what its report says it is
 * doing, and the VLAN its intent names. The one row both surfaces render.
 * `notApplied` is the site's work list's mark (#1630), which the table reads
 * and the device page does not; absent, the row claims nothing either way. */
export function AccessPointPortRow({ row, intentStatus, state, notApplied }: { row: AccessPointRow; intentStatus: string; state: RowState; notApplied?: NotAppliedPort }) {
  return (
    <tr>
      <td><code>{row.port}</code></td>
      <td><StateCell row={row} state={state} /></td>
      <td><Vlans intent={row.intent} status={intentStatus} family="ap" notApplied={notApplied} /></td>
    </tr>
  );
}

/** The access point's wired ports on its own device page (#747, #1353): its
 * own report's ports with their link, and the intent Design holds for each,
 * read when the page opens and each time the operator asks, stored nowhere
 * and never polled. */
export function AccessPointPortsPanel({ device, mayConfigure }: { device: Device; mayConfigure: boolean }) {
  const serial = device.serial;
  // The access point's wired port intent: Zenith's own record, the read the
  // VLAN column is answered from.
  const intent = useQuery(() => api.GET("/devices/{serial}/ports", { params: { path: { serial } } }), [serial]);
  // The access point's own report (ADR-0052 decision 7): the read the State
  // column is answered from, and what names its ports.
  const live = useQuery(() => api.GET("/devices/{serial}/ports/state", { params: { path: { serial } } }), [serial]);
  const set = live.status === "ready" ? live.data : undefined;
  const readAt = useMemo(() => (set ? new Date() : undefined), [set]);
  const rows = accessPointPortRows(intent.data?.ports, set);
  const uplinks = set && !set.unanswered ? set.ports.filter((p) => p.upstream) : [];
  return (
    <Card className="panel" title="Ports"
      head={(
        <>
          <Info label="About these ports">
            {/* Restates ADR-0052 decision 1, ADR-0239 decision 4b and §11.3 — a wired port is one concept for
                both families, an access point's are the ports its own report names, and its uplink is not one. */}
            An access point&rsquo;s wired ports are the ones its own state report names, except the one it reports as its uplink, plus any port that has planned settings in Design. The VLAN is the one planned in Design, not what the access point runs. Read-only.
          </Info>
          {/* A link in words, not a second *Change this in Design*: the page's head carries the one (#1801). */}
          {mayConfigure && <ScopeLink to="/design/wired/access-points" title="Plan an access point's wired ports on Design › Wired. They are sent only after you apply a plan.">Plan these ports in Design</ScopeLink>}
        </>
      )}>
      <p className="muted small live-line" aria-live="polite">
        {live.status === "loading" && <>Loading the access point&rsquo;s last state report from the controller&hellip;</>}
        {live.status === "error" && <>Port state could not be read: {live.problem.message}</>}
        {set && (set.unanswered
          ? <>Port state unanswered: {accessPointUnansweredWords(set.unanswered.reason)}. Each row below is a port that has planned settings in Design. None of these rows means a port is down.</>
          : <>Port state as the access point last reported it.{" "}
              {set.reported_at && readAt
                ? <>Reported at {new Date(set.reported_at).toLocaleString()} ({coarseReportAge(new Date(set.reported_at), readAt, "access point")}): the controller returns the newest report it holds, which can be older than the device&rsquo;s last contact.</>
                : <>The report does not say when the access point wrote it.</>}
              {readAt && <> Read from the controller at {readAt.toLocaleTimeString()}, stored nowhere.</>}
            </>)}
        {" "}<button type="button" className="btn btn-quiet" onClick={() => live.reload()} disabled={live.status === "loading"}>Read again</button>
      </p>
      {intent.status === "error" && <ProblemBox problem={intent.problem} />}
      {intent.status === "loading" && !intent.data && <Loading label="Loading the planned port settings" />}
      {rows.length > 0 ? (
        <table className="table" aria-label={`${device.name}'s wired ports`}>
          <thead><tr><th>Port</th><th>State</th><th>Planned VLAN</th></tr></thead>
          <tbody>{rows.map((r) => <AccessPointPortRow key={r.port} row={r} intentStatus={intent.status} state={{ read: live }} />)}</tbody>
        </table>
      ) : intent.status === "ready" && live.status !== "loading" && (
        <p className="muted">
          {set && !set.unanswered
            ? "Its report names no wired port besides its uplink, and no port has planned settings in Design."
            : <>This access point has no planned wired port settings in Design, and its report was not read, so its ports are not listed here.<Info label="About an empty list">That does not mean it has none.</Info></>}
        </p>
      )}
      {uplinks.map((p) => (
        <p key={p.port} className="muted small">
          <code>{p.port}</code> is the port the access point reports as its uplink: {p.link === "up" ? <>up{p.speed_mbps !== undefined && <> at {p.speed_mbps} Mb/s</>}</> : "down"}.
          <Info label="About the uplink">It is not a wired port and has no planned settings. The report does not say which port the access point reaches its controller through.</Info>
        </p>
      ))}
    </Card>
  );
}
